import { isLocalhostHost } from '@/auth/host-parser'

/**
 * The one place that decides which image sources a rich-text surface may load.
 *
 * The SPA runs under a Content-Security-Policy whose `img-src` lists exactly
 * these origins (see the infra repo's SPA policy), so an image node pointing
 * anywhere else would be blocked by the browser at best and, at worst, be a
 * beacon that tells a third party who opened the page. Any such image is drawn
 * as a plain link instead, matching what agents already write.
 *
 * Allowed: the page's own origin, `data:` / same-origin `blob:` URLs, the
 * stage's public CDN (avatars, logos, cached link-preview images) and the
 * stage's uploads bucket (presigned attachment URLs). The stage is derived
 * from the page host exactly as the rest of the SPA does: `*-staging`
 * hosts and localhost are staging, everything else is prod.
 */

const REGION = 'us-west-2'
const APEX = '.3mote.com'

export type ImageStage = 'staging' | 'prod'

function hostnameOf(host: string): string {
  return host.split(':')[0] ?? ''
}

/** Stage whose CDN + uploads bucket the page at `host` talks to. */
export function imageStageForHost(host: string): ImageStage {
  if (isLocalhostHost(host) || hostnameOf(host).endsWith(`-staging${APEX}`)) return 'staging'
  return 'prod'
}

/** Origins (besides the page's own) an image may be loaded from. */
export function allowedImageOrigins(host: string): string[] {
  const stage = imageStageForHost(host)
  const cdn = stage === 'staging' ? 'avatars-staging.3mote.com' : 'avatars.3mote.com'
  return [`https://${cdn}`, `https://next-${stage}-uploads.s3.${REGION}.amazonaws.com`]
}

interface PageLocation {
  origin: string
  host: string
}

function currentPage(): PageLocation {
  if (typeof window === 'undefined') return { origin: 'https://localhost', host: 'localhost' }
  return { origin: window.location.origin, host: window.location.host }
}

/**
 * True when an `<img src>` pointing at `src` is one of our own origins.
 * Empty / unparseable values are not allowed.
 */
export function isAllowedImageSrc(
  src: string | null | undefined,
  page: PageLocation = currentPage(),
): boolean {
  if (!src) return false
  const value = src.trim()
  if (value === '') return false
  let url: URL
  try {
    url = new URL(value, page.origin)
  } catch {
    return false
  }
  if (url.protocol === 'data:') return value.toLowerCase().startsWith('data:image/')
  if (url.protocol === 'blob:') return url.origin === page.origin
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return false
  if (url.origin === page.origin) return true
  return allowedImageOrigins(page.host).includes(url.origin)
}

/**
 * The href to use when an image is shown as a link instead, or null when the
 * source is not a plain web URL at all (`javascript:`, `file:`, …) and so is
 * not worth linking to.
 */
export function externalImageLinkHref(src: string | null | undefined): string | null {
  if (!src) return null
  try {
    const url = new URL(src.trim())
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : null
  } catch {
    return null
  }
}
